/* eslint-disable */
import Axios from 'axios';
import axiosRetry from 'axios-retry';
// import { cacheAdapterEnhancer } from 'axios-extensions';
import { windowStore } from 'util/windowStore';
import { productTypes, cacheKey, APP_NAME } from 'appConstants';
import { sumoLogger } from 'sumoLogger';
import config from 'config';
import { session } from 'util/session';
import {
  path,
  formAxios,
  getToken,
  resetCache,
  handleApiError,
  Headers,
  StatusCode,
  RETRY_ATTEMPS,
  RETRY_DELAY_MSEC,
} from './';
import { updateURL } from './updateURL';

export const axios = Axios.create({
  baseURL: config.apiRootUrl,
  responseType: 'json',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  params: {
    client_ver: config.clientVersion,
    app_name: APP_NAME,
    product: productTypes.TEXT_RECRUIT,
  },
  // I am removing the cache adapter for now as it's causing issues when data gets stale. -Amir
  // We have plans to implement a better caching strategy in the future. See: https://jira.icims.net/browse/TXTE-26524
  // I am leaving below code commented out for now for reference. -Amir
  // adapter: cacheAdapterEnhancer(Axios.defaults.adapter),
});

// interceptor to filter out null values from payload
axios.interceptors.request.use(
  (config) => {
    if (config.params) {
      config.params = Object.fromEntries(
        Object.entries(config.params).filter(([, value]) => value !== null),
      );
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * **********************************
 * ********** RETRY LOGIC ***********
 * **********************************
 */
export const retry = (error) => {
  if (error && error.config) {
    const { message, response } = error;
    const isNetworkError = message === 'Network Error';
    const isUserError = response && response.status === StatusCode.BAD;
    if (isNetworkError || isUserError) {
      return false;
    }
  }
  return true;
};

axiosRetry(axios, {
  retries: RETRY_ATTEMPS,
  retryCondition: (error) => retry(error),
  retryDelay: () => RETRY_DELAY_MSEC,
});

// I am removing the cache adapter for now as it's causing issues when data gets stale. -Amir
// We have plans to implement a better caching strategy in the future. See: https://jira.icims.net/browse/TXTE-26524
// I am leaving below code commented out for now for reference. -Amir
// API paths with fresh responses (not cached)
// const doNotCache = new Set([
//   // Auth management
//   path.TOKEN_INFO,
//   path.LOGIN,
//   path.LOGOUT,
//   path.WEBSOCKET_ID,

//   // Events must always get a fresh request
//   path.EVENTS,
//   path.CANDIDATES,
//   path.GET_CANDIDATE_BY_ID,

//   // Users
//   path.MGR_USER,
//   path.MGR_USERS,

//   // Downloads
//   path.BACKGROUND_EVENTS,

//   // Fetching converstation history
//   path.GET_MESSAGES_API,

//   // Fetching inbox threads
//   path.MESSAGE_THREADS,

//   // Fetching unread message(s)
//   path.MESSAGE,

//   // Sending single msg - sounds like a POST but since it's a GET request
//   // we need to add it here.
//   path.SEND_MESSAGE,

//   // fetching tags
//   path.TAGS,

//   //fetching tag
//   path.TAG,

//   //fetching tag
//   path.TAG,

//   path.MESSAGE_TEMPLATES,

//   // fetching scheduled messages on dashboard and campaigns
//   path.SCHEDULED_MESSAGES_SEARCH,
//   path.SCHEDULED_MESSAGES,
//   path.GET_SCHEDULED_MESSAGE,

//   // fetching ari intents
//   path.FETCH_ARI_INTENTS,

//   // events
//   path.EVENTS,
//   path.GET_EVENT,
//   path.GENERATE_EVENT_LINK,

//   // teams
//   path.TEAMS,
//   path.GET_TEAM,

//   //ari responses modal should always get a fresh request
//   path.ARI_EDIT_RESPONSES,

//   // candidates
//   path.VALIDATE_CANDIDATE,

//   /**
//    * TODO: forceUpdate ensures this API resp. is not cached.
//    *
//    * It's because response.candidates data will not be updated
//    * if the same campaign has been added to a other candidate(s)
//    * within the last 5 mins.
//    *
//    * Ideally we can cache it and update response.candidates
//    * when the same campaign has been added to other candidate(s).
//    * The solution is a little complicated and for now we can
//    * punt on caching. -Amir
//    */
//   path.CAMPAIGNS_API,
// ]);

/**
 * ********************************************
 * *********** REQUEST INTERCEPTORS ***********
 * ********************************************
 */
axios.interceptors.request.use(
  async (conf) => {
    const onLookup = conf.url.includes(path.LOOKUP);
    const onAuthenticate = conf.url.includes(path.AUTHENTICATE);
    const onForgotPw = conf.url.includes(path.FORGOT_PASSWORD);
    const onAuthInfo = conf.url.includes(path.REMOTE_AUTH_INFO);
    const onExpirePw = conf.url.includes(path.CHANGE_PASSWORD);
    const onRecoverPw = conf.url.includes(path.RECOVER_PASSWORD);
    /**
     * No need to retrieve auth token(s) for:
     * - Lookup service: /lookup
     * - SSO: /remote_auth_info
     * - Login: /authenticate
     * - ExpiredPassword: /change_password
     * - RecoverPassword: /recover_password
     */
    if (!onLookup && !onAuthInfo && !onAuthenticate && !onForgotPw && !onExpirePw && !onRecoverPw) {
      // Set http headers with required token(s)
      await getToken(conf);
    }

    // I am removing the cache adapter for now as it's causing issues when data gets stale. -Amir
    // We have plans to implement a better caching strategy in the future. See: https://jira.icims.net/browse/TXTE-26524
    // I am leaving below code commented out for now for reference. -Amir
    // if (doNotCache.has(conf.url)) {
    //   conf.forceUpdate = true;
    // }

    // prepend version and product for the baseURL
    updateURL(conf);

    return conf;
  },
  (error) => Promise.reject(error),
);

/**
 * ***************************************
 * ********** Helper Functions ***********
 * ***************************************
 */

export const clearDefaults = () => {
  // Clear axios default params & headers
  delete axios.defaults.params.account_id;
  delete axios.defaults.params.customer_id;
  delete axios.defaults.headers[Headers.SessionToken];
  delete axios.defaults.headers[Headers.Authorization];
  delete axios.defaults.headers[Headers.UserInfo];

  /**
   * Reset auth0 flag, and
   * Remove all app related cached data from web storage
   */
  windowStore.auth0 = false;
  windowStore.clear();
};

export const getAccountId = (userInfo) => {
  try {
    return userInfo.customerIds.filter((c) => c.product === productTypes.TEXT_RECRUIT)[0]
      .customerId;
  } catch (e) {
    sumoLogger.log({
      error: e,
      additionalInfo: {
        message: 'Error retrieving account ID',
        userInfo,
      },
    });
    return null;
  }
};

export const getProductType = (appName) => {
  // TODO : other product type need to be added
  if (appName === 'textreach') {
    return productTypes.TEXT_REACH;
  }
  return productTypes.TEXT_RECRUIT;
};

export const updateApiBaseUrl = (cloudEnvironment, cacheIt = true, appName = 'textrecruit') => {
  if (!cloudEnvironment) {
    return;
  }

  let apiRoot = config.apiRootUrl;
  let rtPath = config.realTimeUrl;

  if (cloudEnvironment.startsWith('azure')) {
    apiRoot = `https://api-azure-us.${config.envShortName}.textrecruit.tools`;
    rtPath = `wss://rt-azure-us.${config.envShortName}.textrecruit.tools/ws`;
  } else if (cloudEnvironment.startsWith('awsgov')) {
    apiRoot = 'https://fed1.textrecruit.com/api/';
    rtPath = 'wss://rt-awsgov-us.prd.textrecruit.tools/ws';
  } else if (cloudEnvironment === 'awseu-prd') {
    apiRoot = config.euApiRootUrl;
    rtPath = config.euRealTimeUrl;
  } else if (cloudEnvironment === 'aws-prd-ups') {
    apiRoot = 'https://api-aws-us.prd-ups.textrecruit.tools/api/';
    rtPath = 'wss://rt-aws-us.prd-ups.textrecruit.tools/';
  }

  /*
   * Update:
   *   - Axios base URL
   *   - Real-time server url, and
   *   - localStorage.ext.apiRoot/rtPath
   */
  axios.defaults.baseURL = apiRoot;
  axios.defaults.params.product = getProductType(appName);
  formAxios.defaults.baseURL = `${apiRoot}v1/${appName}/`;
  config.realTimeUrl = rtPath;
  if (cacheIt) {
    windowStore.set(cacheKey.CLOUD_ENV, cloudEnvironment);
  }
};

/**
 * ********************************************
 * ********** RESPONSE INTERCEPTORS ***********
 * ********************************************
 */
axios.interceptors.response.use(
  (response) => {
    const { url } = response.config;
    const responseData = response.data ? response.data : {};
    const { token = '', userName = '' } = responseData;

    if (url.includes(path.TOKEN_INFO) || url.includes(path.AUTHENTICATE)) {
      const accountId = getAccountId(response.data);
      axios.defaults.params.account_id = accountId;
      axios.defaults.params.customer_id = accountId;
      formAxios.defaults.params.account_id = accountId;
      formAxios.defaults.params.customer_id = accountId;
      windowStore.username = userName;
      windowStore.accountId = accountId;

      // Update new/refreshed token in cache
      if (!windowStore.auth0) {
        windowStore.setToken(token);
      }

      // Run refresh token logic
      session.refreshTokenInterval();
    }
    if (url.includes(path.LOGOUT)) {
      clearDefaults();

      // Stop refresh token logic
      session.clearRefreshTokenInterval();
    }
    return response.data;
  },
  (error) => {
    const errorResp = error.response;
    if (errorResp && errorResp !== StatusCode.BAD) {
      const url = error.config.url.includes('password')
        ? error.config.url.split('&password=')[0]
        : error.config.url;
      sumoLogger.log({
        error: errorResp.data,
        userAgent: navigator.userAgent,
        params: error.config.params,
        apiUrl: url,
      });
    }

    if (
      errorResp &&
      errorResp.data &&
      errorResp.data.apiResult &&
      errorResp.data.apiResult.message === 'Token has expired'
    ) {
      resetCache();
    }

    if (error.message === 'Network Error') {
      handleApiError();
      return Promise.reject(error.toJSON());
    } else if (errorResp) {
      let errorData = {};
      if (typeof errorResp.data === 'string') {
        errorData.message = errorResp.data;
      } else if (errorResp.data === null) {
        errorData.message = 'Error';
      } else if (errorResp.data.apiResult) {
        errorData = { ...errorResp.data.apiResult, ...errorResp.data };
      } else {
        errorData = { ...errorResp.data };
      }
      return Promise.reject(errorData);
    }
    return Promise.reject(error);
  },
);
